import { LocalStorageKeys, ResourceName } from "@/utils";
import { Close, Download, ExpandMore, Search } from "@mui/icons-material";
import {
  Button,
  capitalize,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";
import { FC, useState } from "react";
import { TopToolbar as RaTopToolbar, useListContext } from "react-admin";
import { Modal } from "@/components";
import { PaymentsFilter } from "@/features/affiliate-payouts/modals";
import { FiltersList } from "@/features/affiliate-payouts/components/top-toolbar/filters-list.component";
import { useDebouncedListSearch, useDownloadFile } from "@/hooks";
import { SortDirection } from "@/api/shared.ts";
import { FileFormat } from "@/types";
import { AffiliateApi } from "@/api/affiliates";

const TopToolbar: FC = () => {
  const { filterValues, sort } = useListContext();
  const [isFiltersModalOpened, setIsFiltersModalOpened] = useState(false);

  const { isLoading: isXlsxDownloading, download: downloadXlsx } =
    useDownloadFile({
      apiFunc: AffiliateApi.downloadXlsx,
      localStorageFileKey: LocalStorageKeys.affiliatePaymentsXlsxReport,
      format: FileFormat.XLSX,
      filterValues,
      sort: {
        sortBy: sort.field,
        sortDirection: sort.order.toLowerCase() as SortDirection,
      },
    });

  const isUsersFetching = useIsFetching({
    queryKey: [ResourceName.AFFILIATE_PAYOUTS, "getList"],
  });

  const [searchQuery, setSearchQuery] = useDebouncedListSearch();

  return (
    <>
      {/* Modal used here due to RA listContext */}
      <Modal
        title="Filters"
        isOpened={isFiltersModalOpened}
        onClose={() => setIsFiltersModalOpened(false)}
        children={
          <PaymentsFilter onModalClose={() => setIsFiltersModalOpened(false)} />
        }
      />

      <RaTopToolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          marginTop: 1,
          paddingRight: 0,
          paddingLeft: 0,
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <Chip
            size="small"
            variant="filled"
            label={`Sorted by: ${capitalize(sort.field)}`}
          />

          <Stack direction={"row"} spacing={1}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search by affiliate"
              sx={{ width: "220px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 50 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {!!isUsersFetching && <CircularProgress size={16} />}
                    {!isUsersFetching && !!searchQuery && (
                      <Close
                        sx={{ cursor: "pointer" }}
                        onClick={() => setSearchQuery("")}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              endIcon={<ExpandMore />}
              sx={{ minWidth: "auto" }}
              onClick={() => setIsFiltersModalOpened(true)}
            >
              Filters
            </Button>
            <Button
              size="small"
              endIcon={<Download />}
              sx={{ minWidth: "auto" }}
              loading={isXlsxDownloading}
              disabled={isXlsxDownloading}
              onClick={downloadXlsx}
            >
              Download XLSX
            </Button>
          </Stack>
        </Stack>
        <FiltersList />
      </RaTopToolbar>
    </>
  );
};

export { TopToolbar };
